import { TaskType } from "@common/types/task";
import type { View } from "@common/types/views/views";
import {
    applyReportDisplayPatch,
    readReportDisplayFilters,
    ReportDisplayFiltersPatch,
    ReportTaskType,
} from "@components/reports/reportDisplayFilters";
import { ReportDisplayFields } from "@components/reports/ReportDisplayFields";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import {
    parseBalanceSheetCloseTemplateViewConfig,
    parseProfitLossCloseTemplateViewConfig,
    readBalanceSheetMonthsFromViewConfig,
    readProfitLossMonthsFromViewConfig,
    serializeBalanceSheetCloseTemplateViewConfigWithMonths,
    serializeProfitLossCloseTemplateViewConfigWithMonths,
} from "./closeTemplateTaskViewConfig";

type StoredViewConfig = ReturnType<
    typeof serializeProfitLossCloseTemplateViewConfigWithMonths
>;

interface ReportViewConfigCodec {
    parse: (viewConfig: StoredViewConfig | null) => View;
    readMonths: (viewConfig: StoredViewConfig | null) => number | null;
    serializeWithMonths: (
        view: View,
        months: number | null,
    ) => StoredViewConfig;
}

const REPORT_VIEW_CONFIG_CODECS: Record<ReportTaskType, ReportViewConfigCodec> =
    {
        [TaskType.PROFIT_LOSS]: {
            parse: parseProfitLossCloseTemplateViewConfig,
            readMonths: readProfitLossMonthsFromViewConfig,
            serializeWithMonths:
                serializeProfitLossCloseTemplateViewConfigWithMonths,
        },
        [TaskType.BALANCE_SHEET]: {
            parse: parseBalanceSheetCloseTemplateViewConfig,
            readMonths: readBalanceSheetMonthsFromViewConfig,
            serializeWithMonths:
                serializeBalanceSheetCloseTemplateViewConfigWithMonths,
        },
    };

interface Props {
    htmlIdPrefix: string;
    taskType: ReportTaskType;
    viewConfig: StoredViewConfig | null;
    onViewConfigChange: React.Dispatch<
        React.SetStateAction<StoredViewConfig | null>
    >;
}

interface SerializePeriodDateViewConfigParams {
    taskType: ReportTaskType;
    viewConfig: StoredViewConfig | null;
    months: number | null;
}

export function serializePeriodDateViewConfig({
    taskType,
    viewConfig,
    months,
}: SerializePeriodDateViewConfigParams): StoredViewConfig {
    const codec = REPORT_VIEW_CONFIG_CODECS[taskType];
    return codec.serializeWithMonths(codec.parse(viewConfig), months);
}

interface SerializeReportDisplayViewConfigParams {
    taskType: ReportTaskType;
    viewConfig: StoredViewConfig | null;
    patch: ReportDisplayFiltersPatch;
}

export function serializeReportDisplayViewConfig({
    taskType,
    viewConfig,
    patch,
}: SerializeReportDisplayViewConfigParams): StoredViewConfig {
    const codec = REPORT_VIEW_CONFIG_CODECS[taskType];
    const view = codec.parse(viewConfig);
    return codec.serializeWithMonths(
        {
            ...view,
            filters: applyReportDisplayPatch({
                filters: view.filters,
                patch,
            }),
        },
        codec.readMonths(viewConfig),
    );
}

const MONTH_ITEMS: Array<PickerItemData<number>> = Array.from(
    { length: 13 },
    (_, month) => ({ value: month, label: String(month) }),
);

export const PeriodDateFields = ({
    htmlIdPrefix,
    taskType,
    viewConfig,
    onViewConfigChange,
}: Props) => {
    const codec = REPORT_VIEW_CONFIG_CODECS[taskType];
    const currentMonths = codec.readMonths(viewConfig);
    const displayValue = readReportDisplayFilters(
        codec.parse(viewConfig).filters,
    );

    return (
        <>
            <DescriptionListItem
                label="Months before close period"
                htmlFor={`${htmlIdPrefix}-numberOfMonthsToShowBeforeCurrentClosePeriods`}
            >
                <div className={taskStyles.detailSelectWrapper}>
                    <Picker.Select
                        id={`${htmlIdPrefix}-numberOfMonthsToShowBeforeCurrentClosePeriods`}
                        items={MONTH_ITEMS}
                        value={currentMonths ?? null}
                        placeholder="Select months"
                        trigger={
                            <Picker.Button
                                variant="primary"
                                className={taskStyles.detailSelectTrigger}
                            />
                        }
                        onValueChange={(value) => {
                            const months =
                                value !== null && value >= 0 && value <= 12
                                    ? value
                                    : null;
                            onViewConfigChange((prev) =>
                                serializePeriodDateViewConfig({
                                    taskType,
                                    viewConfig: prev,
                                    months,
                                }),
                            );
                        }}
                    />
                </div>
            </DescriptionListItem>
            <ReportDisplayFields
                taskType={taskType}
                value={displayValue}
                wrapperClassName={taskStyles.detailSelectWrapper}
                triggerClassName={taskStyles.detailSelectTrigger}
                onChange={(patch) =>
                    onViewConfigChange((prev) =>
                        serializeReportDisplayViewConfig({
                            taskType,
                            viewConfig: prev,
                            patch,
                        }),
                    )
                }
            />
        </>
    );
};
